import React, { useState, useEffect } from 'react';
import { useEnhancedApi } from '../hooks/useEnhancedApi';
import { getThemeClasses, getButtonClasses } from '../utils/themeUtils';
import EnhancedErrorDisplay from './EnhancedErrorDisplay';
import EnhancedLoadingState from './EnhancedLoadingState';

/**
 * Enhanced knowledge base sync status component with user-friendly messaging
 * and retry functionality, hiding technical details like ElevenLabs and RAG
 */
const EnhancedKnowledgeBaseSyncStatus = ({ 
  file, 
  onRetry, 
  onStatusChange,
  showRetryButton = true,
  compact = false 
}) => {
  const { executeWithRetry, loading, error } = useEnhancedApi({
    component: 'KnowledgeBaseSyncStatus'
  });
  
  const [syncStatus, setSyncStatus] = useState(file?.sync_status || 'pending');
  const [lastSyncAttempt, setLastSyncAttempt] = useState(file?.last_sync_attempt);
  const [syncError, setSyncError] = useState(file?.sync_error);
  const [isRetrying, setIsRetrying] = useState(false);

  const theme = getThemeClasses();

  // Update local state when file prop changes
  useEffect(() => {
    if (file) {
      setSyncStatus(file.sync_status || 'pending');
      setLastSyncAttempt(file.last_sync_attempt);
      setSyncError(file.sync_error);
    }
  }, [file]);

  // Notify parent of status changes
  useEffect(() => {
    if (onStatusChange) {
      onStatusChange(syncStatus);
    }
  }, [syncStatus, onStatusChange]);

  /**
   * Get user-friendly status information
   */
  const getStatusInfo = () => {
    // If file is assigned to one or more parrots but backend status still says 'not_assigned',
    // treat it as queued/pending for a consistent UX.
    const effectiveStatus = (file?.parrotUsage?.count > 0 && (syncStatus === 'not_assigned' || !syncStatus))
      ? 'pending'
      : syncStatus;

    switch (effectiveStatus) {
      case 'not_assigned':
        return {
          label: 'Not Assigned',
          description: 'File is uploaded but not assigned to any parrot yet',
          color: 'text-gray-600 dark:text-gray-400',
          bgColor: 'bg-gray-50 dark:bg-gray-900/20',
          borderColor: 'border-gray-200 dark:border-gray-800',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ),
          canRetry: false
        };
      
      case 'pending':
        return {
          label: 'Waiting to Process',
          description: 'Your file is queued for processing',
          color: 'text-yellow-600 dark:text-yellow-400',
          bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
          borderColor: 'border-yellow-200 dark:border-yellow-800',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          canRetry: false
        };
      
      case 'processing':
      case 'syncing':
        return {
          label: 'Processing',
          description: 'Your file is being processed and will be available soon',
          color: 'text-blue-600 dark:text-blue-400',
          bgColor: 'bg-blue-50 dark:bg-blue-900/20',
          borderColor: 'border-blue-200 dark:border-blue-800',
          icon: (
            <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          ),
          canRetry: false
        };
      
      case 'completed':
      case 'synced':
        return {
          label: 'Ready',
          description: 'Your file has been processed and is ready to use',
          color: 'text-green-600 dark:text-green-400',
          bgColor: 'bg-green-50 dark:bg-green-900/20',
          borderColor: 'border-green-200 dark:border-green-800',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ),
          canRetry: false
        };
      
      case 'failed':
      case 'error':
        return {
          label: 'Processing Failed',
          description: 'There was an issue processing your file. You can try again.',
          color: 'text-red-600 dark:text-red-400',
          bgColor: 'bg-red-50 dark:bg-red-900/20',
          borderColor: 'border-red-200 dark:border-red-800',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          ),
          canRetry: true
        };
      
      default:
        return {
          label: 'Unknown Status',
          description: 'Status information is not available',
          color: 'text-gray-600 dark:text-gray-400',
          bgColor: 'bg-gray-50 dark:bg-gray-900/20',
          borderColor: 'border-gray-200 dark:border-gray-800',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          canRetry: false
        };
    }
  };

  /**
   * Handle retry sync operation
   */
  const handleRetry = async () => {
    if (!file?.id) return;

    setIsRetrying(true);
    try {
      await executeWithRetry(async (api) => {
        const response = await api.post(`/api/knowledge-base/${file.id}/retry-sync`);
        
        // Invalidate cache after retry sync
        api.invalidateCache('/api/knowledge-base');
        
        // Update local status
        setSyncStatus('pending');
        setSyncError(null);
        setLastSyncAttempt(new Date().toISOString());
        
        return response;
      }, {
        operation: 'Retry Knowledge Base Sync',
        successMessage: 'File queued for reprocessing'
      });

      if (onRetry) {
        onRetry(file.id);
      }
    } catch (err) {
      console.error('Failed to retry sync:', err);
    } finally {
      setIsRetrying(false);
    }
  };

  const statusInfo = getStatusInfo();

  if (compact) {
    return (
      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color} ${statusInfo.bgColor} ${statusInfo.borderColor} border`}>
        <span className="mr-1">{statusInfo.icon}</span>
        {statusInfo.label}
        {statusInfo.canRetry && showRetryButton && (
          <button
            onClick={handleRetry}
            disabled={isRetrying || loading}
            className="ml-2 text-xs hover:underline disabled:opacity-50"
          >
            {isRetrying ? 'Retrying...' : 'Retry'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-lg border p-4 ${statusInfo.bgColor} ${statusInfo.borderColor}`}>
      <div className="flex items-start space-x-3">
        <div className={`flex-shrink-0 ${statusInfo.color}`}>
          {statusInfo.icon}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className={`text-sm font-medium ${statusInfo.color}`}>
                {statusInfo.label}
              </h4>
              <p className={`mt-1 text-sm ${statusInfo.color} opacity-90`}>
                {statusInfo.description}
              </p>
              
              {/* Show last sync attempt for failed status */}
              {syncStatus === 'failed' && lastSyncAttempt && (
                <p className={`mt-1 text-xs ${statusInfo.color} opacity-75`}>
                  Last attempt: {new Date(lastSyncAttempt).toLocaleString()}
                </p>
              )}
              
              {/* Show user-friendly error message */}
              {syncStatus === 'failed' && syncError && (
                <div className="mt-2">
                  <p className={`text-xs ${statusInfo.color} opacity-90`}>
                    {getUserFriendlyErrorMessage(syncError)}
                  </p>
                </div>
              )}
            </div>
            
            {/* Retry button for failed status */}
            {statusInfo.canRetry && showRetryButton && (
              <div className="flex-shrink-0 ml-4">
                <button
                  onClick={handleRetry}
                  disabled={isRetrying || loading}
                  className={getButtonClasses('outline', 'small', isRetrying || loading)}
                >
                  {isRetrying ? (
                    <>
                      <EnhancedLoadingState variant="spinner" size="small" className="mr-1" />
                      Retrying...
                    </>
                  ) : (
                    'Try Again'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Show error details if available */}
      {error && (
        <div className="mt-3">
          <EnhancedErrorDisplay 
            error={error} 
            onRetry={handleRetry}
            compact={true}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Convert technical error messages to user-friendly ones
 */
const getUserFriendlyErrorMessage = (technicalError) => {
  if (!technicalError) return 'An unknown error occurred.';
  
  const errorLower = technicalError.toLowerCase();
  
  if (errorLower.includes('elevenlabs') || errorLower.includes('api')) {
    return 'Unable to connect to processing service. Please try again later.';
  }
  
  if (errorLower.includes('timeout')) {
    return 'Processing took too long. Please try again.';
  }
  
  if (errorLower.includes('file') && errorLower.includes('corrupt')) {
    return 'The file appears to be corrupted. Please try uploading again.';
  }
  
  if (errorLower.includes('size') || errorLower.includes('large')) {
    return 'The file is too large to process. Please try a smaller file.';
  }
  
  if (errorLower.includes('format') || errorLower.includes('type')) {
    return 'The file format is not supported. Please try a different file.';
  }
  
  if (errorLower.includes('quota') || errorLower.includes('limit')) {
    return 'Processing limit reached. Please try again later.';
  }
  
  if (errorLower.includes('network') || errorLower.includes('connection')) {
    return 'Network connection issue. Please check your connection and try again.';
  }
  
  // Default fallback for unknown errors
  return 'Processing failed. Please try again or contact support if the issue persists.';
};

/**
 * Bulk sync status component for multiple files
 */
export const BulkSyncStatus = ({ files, onRetryAll, className = '' }) => {
  const theme = getThemeClasses();
  
  const statusCounts = files.reduce((acc, file) => {
    const status = file.sync_status || 'pending';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
  
  const totalFiles = files.length;
  const completedFiles = statusCounts.completed || statusCounts.synced || 0;
  const failedFiles = statusCounts.failed || statusCounts.error || 0;
  const processingFiles = statusCounts.processing || statusCounts.syncing || 0;
  const pendingFiles = statusCounts.pending || 0;
  
  const completionPercentage = totalFiles > 0 ? Math.round((completedFiles / totalFiles) * 100) : 0;
  
  return (
    <div className={`rounded-lg border p-4 ${theme.bg.card} ${theme.border.primary} ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className={`text-sm font-medium ${theme.text.primary}`}>
          Processing Status
        </h3>
        {failedFiles > 0 && onRetryAll && (
          <button
            onClick={onRetryAll}
            className={getButtonClasses('outline', 'small')}
          >
            Retry Failed ({failedFiles})
          </button>
        )}
      </div>
      
      {/* Progress bar */}
      <div className="mb-3">
        <div className="flex justify-between text-sm mb-1">
          <span className={theme.text.secondary}>
            {completedFiles} of {totalFiles} files ready
          </span>
          <span className={theme.text.secondary}>
            {completionPercentage}%
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div 
            className="bg-teal-500 dark:bg-teal-400 h-2 rounded-full transition-all duration-300"
            style={{ width: `${completionPercentage}%` }}
          ></div>
        </div>
      </div>
      
      {/* Status breakdown */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        {completedFiles > 0 && (
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
            <span className={theme.text.secondary}>
              {completedFiles} Ready
            </span>
          </div>
        )}
        
        {processingFiles > 0 && (
          <div className="flex items-center">
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-2 animate-pulse"></div>
            <span className={theme.text.secondary}>
              {processingFiles} Processing
            </span>
          </div>
        )}
        
        {pendingFiles > 0 && (
          <div className="flex items-center">
            <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
            <span className={theme.text.secondary}>
              {pendingFiles} Waiting
            </span>
          </div>
        )}
        
        {failedFiles > 0 && (
          <div className="flex items-center">
            <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
            <span className={theme.text.secondary}>
              {failedFiles} Failed
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedKnowledgeBaseSyncStatus;
